import { config } from "config/config";
import {
  shouldEnableYeloAppAnalytics,
  yeloAppAnalyticsBaseProps,
  yeloAppAnalyticsUserId,
} from "utils/analytics.util";

type MixpanelClient = {
  init: (token: string, config?: Record<string, unknown>) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (id: string) => void;
  people: { set: (props: Record<string, unknown>) => void };
};

let loading: Promise<MixpanelClient | null> | null = null;

function getMixpanel(): Promise<MixpanelClient | null> {
  if (typeof window === "undefined" || !shouldEnableYeloAppAnalytics()) {
    return Promise.resolve(null);
  }

  const token = config.MIXPANEL_TOKEN;
  if (!token) {
    return Promise.resolve(null);
  }

  if (!loading) {
    loading = import("mixpanel-browser").then(
      ({ default: mixpanel }) => {
        try {
          mixpanel.init(token, {
            debug: false,
            track_pageview: false,
            persistence: "localStorage",
            api_host: "https://api-eu.mixpanel.com",
          });
          return mixpanel;
        } catch (error) {
          console.error("[Mixpanel] Failed to initialize:", error);
          return null;
        }
      },
      (error) => {
        console.error("[Mixpanel] Failed to load:", error);
        loading = null;
        return null;
      }
    );
  }

  return loading;
}

function track(event: string, properties?: Record<string, unknown>) {
  void getMixpanel().then((client) => client?.track(event, properties));
}

function dedupeSessionKey(event: string, id: string) {
  return `mixpanel:${event}:${id}`;
}

function trackOncePerSession(
  event: string,
  id: string,
  properties: Record<string, unknown>
) {
  if (typeof window === "undefined" || !id) {
    track(event, properties);
    return;
  }
  const key = dedupeSessionKey(event, id);
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, "1");
  track(event, properties);
}

export function identifyYeloAppUser() {
  const userId = yeloAppAnalyticsUserId();
  if (!userId) return;

  void getMixpanel().then((client) => {
    if (!client) return;
    client.identify(userId);
    client.people.set({ partner_source: "yelo_app" });
  });
}

/** @deprecated Use identifyYeloAppUser */
export const identifyPartnerUser = identifyYeloAppUser;

export const trackScreenPartnerPickup = () => {
  track("PARTNER_SCREEN_PICKUP", yeloAppAnalyticsBaseProps("pickup"));
};

export const trackPartnerSearchPrices = (properties: {
  trip_type: string;
  city?: string;
}) => {
  track("PARTNER_ACTION_SEARCH_PRICES", {
    ...yeloAppAnalyticsBaseProps("pickup"),
    ...properties,
  });
};

export const trackScreenTripBooking = () => {
  track("SCREEN_TRIP_BOOKING", yeloAppAnalyticsBaseProps("search_form"));
};

export const trackPartnerBookingStep = (properties: {
  step: "classes" | "additional_form" | "checkout";
}) => {
  track("PARTNER_BOOKING_STEP", {
    ...yeloAppAnalyticsBaseProps("search_form"),
    ...properties,
  });
};

export const trackButtonBookTrip = (properties: {
  vehicle_class?: string;
  price?: number;
  trip_type?: string;
}) => {
  track("BUTTON_BOOK_TRIP", {
    ...yeloAppAnalyticsBaseProps("checkout"),
    ...properties,
    timestamp: Date.now(),
  });
};

export const trackPartnerBookingCreated = (properties: {
  trip_id: string;
  payment_gateway?: string;
  amount?: number;
  currency?: string;
}) => {
  track("PARTNER_ACTION_BOOKING_CREATED", {
    ...yeloAppAnalyticsBaseProps("checkout"),
    ...properties,
  });
};

export const trackPartnerScreenPayment = (properties: {
  trip_id: string;
  payment_gateway?: string;
}) => {
  track("PARTNER_SCREEN_PAYMENT", {
    ...yeloAppAnalyticsBaseProps("payment_form"),
    ...properties,
  });
};

export const trackPaymentSuccess = (properties: {
  trip_id: string;
  payment_method?: string;
  amount?: number;
  currency?: string;
}) => {
  trackOncePerSession("PAYMENT_SUCCESS", properties.trip_id, {
    ...yeloAppAnalyticsBaseProps("payment_success"),
    payment_method: properties.payment_method || "card",
    ...properties,
  });
};

export const trackPaymentFailed = (properties: {
  trip_id?: string;
  error_message?: string;
}) => {
  track("PAYMENT_FAILED", {
    ...yeloAppAnalyticsBaseProps("payment_form"),
    ...properties,
  });
};

export const trackPartnerBookingSuccess = (properties: { trip_id: string }) => {
  trackOncePerSession("PARTNER_SCREEN_BOOKING_SUCCESS", properties.trip_id, {
    ...yeloAppAnalyticsBaseProps("booking_success"),
    ...properties,
  });
};
